import type { ShipSpec } from './ship.js';
import {
  SHIP_NUMERIC_STATS,
  type ComponentModule,
  type Hardpoint,
  type HardpointSize,
  type Loadout,
  type LoadoutTotals,
  type Module,
  type ModuleCatalog,
  type StatTotals,
  type WeaponModule,
  type WeaponProfile,
  type EffectiveShip,
} from './modules.js';

const SIZE_ORDER: Record<HardpointSize, number> = { small: 0, medium: 1, large: 2 };

const makeStatTotals = (seed: number): StatTotals =>
  Object.fromEntries(SHIP_NUMERIC_STATS.map((stat) => [stat, seed])) as StatTotals;

const emptyTotals = (): LoadoutTotals => ({
  moduleMassKg: 0,
  powerDraw: 0,
  weaponCount: 0,
  componentCount: 0,
  modifierAdds: makeStatTotals(0),
  modifierMuls: makeStatTotals(1),
});

const resolveModule = (
  hardpoint: Hardpoint,
  loadout: Loadout,
  catalog: ModuleCatalog,
): Module | null => {
  const moduleId = loadout.fitted[hardpoint.id];
  if (!moduleId) return null;
  const module = catalog[moduleId];
  return module && validateFit(hardpoint, module) ? module : null;
};

const collectFittedModules = (
  loadout: Loadout,
  catalog: ModuleCatalog,
): Array<{ hardpoint: Hardpoint; module: Module }> => {
  const fitted: Array<{ hardpoint: Hardpoint; module: Module }> = [];
  for (const hardpoint of loadout.hardpoints) {
    const module = resolveModule(hardpoint, loadout, catalog);
    if (module) fitted.push({ hardpoint, module });
  }
  return fitted;
};

const mergeModifiers = (totals: LoadoutTotals, module: ComponentModule): void => {
  for (const stat of SHIP_NUMERIC_STATS) {
    totals.modifierAdds[stat] += module.modifiers[`${stat}Add`] ?? 0;
    totals.modifierMuls[stat] *= module.modifiers[`${stat}Mul`] ?? 1;
  }
};

const sumTotals = (fitted: Array<{ hardpoint: Hardpoint; module: Module }>): LoadoutTotals => {
  const totals = emptyTotals();
  for (const { module } of fitted) {
    totals.moduleMassKg += module.massKg;
    totals.powerDraw += module.powerDraw;
    if (module.kind === 'weapon') totals.weaponCount += 1;
    else {
      totals.componentCount += 1;
      mergeModifiers(totals, module);
    }
  }
  return totals;
};

const isOverBudget = (hardpoint: Hardpoint, module: Module): boolean =>
  hardpoint.powerBudget > 0 && module.powerDraw > hardpoint.powerBudget;

const applyStatModifiers = (spec: ShipSpec, totals: LoadoutTotals): ShipSpec => {
  const next = { ...spec, massKg: spec.massKg + totals.moduleMassKg };
  for (const stat of SHIP_NUMERIC_STATS) next[stat] = spec[stat] * totals.modifierMuls[stat] + totals.modifierAdds[stat];
  return next;
};

const toWeaponProfile = (
  hardpoint: Hardpoint,
  module: WeaponModule,
  overPower: boolean,
): WeaponProfile => ({
  hardpointId: hardpoint.id,
  moduleId: module.id,
  name: module.name,
  size: module.size,
  mountType: hardpoint.mountType,
  paletteRole: module.paletteRole,
  transform: hardpoint.transform,
  damage: module.damage,
  projectileSpeedKmS: module.projectileSpeedKmS,
  cooldownSec: module.cooldownSec,
  energyCost: module.energyCost,
  rangeKm: module.rangeKm,
  powerDraw: module.powerDraw,
  overPower,
});

export const validateFit = (hardpoint: Hardpoint, module: Module): boolean =>
  hardpoint.kind === module.kind && SIZE_ORDER[module.size] <= SIZE_ORDER[hardpoint.size];

export const recomputeTotals = (
  loadout: Loadout,
  catalog: ModuleCatalog,
): LoadoutTotals => sumTotals(collectFittedModules(loadout, catalog));

export const applyLoadout = (
  spec: ShipSpec,
  loadout: Loadout,
  catalog: ModuleCatalog,
): EffectiveShip => {
  const fitted = collectFittedModules(loadout, catalog);
  const totals = sumTotals(fitted);
  const weapons: WeaponProfile[] = [];
  let overPower = false;
  for (const { hardpoint, module } of fitted) {
    const moduleOverPower = isOverBudget(hardpoint, module);
    overPower = overPower || moduleOverPower;
    if (module.kind === 'weapon') weapons.push(toWeaponProfile(hardpoint, module, moduleOverPower));
  }
  return { baseSpec: spec, spec: applyStatModifiers(spec, totals), loadout, totals, weapons, overPower };
};
